import React, { useState, forwardRef, useEffect } from "react";
import {
  Send,
  Loader2,
  Zap,
  Database,
  FileText,
  ChevronUp,
} from "lucide-react";
import { Button } from "../ui/Button";
import { validateInput } from "../../utils/security";

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  isMaximized?: boolean;
  themeMode?: "light" | "dark";
  accessMode?: "user" | "admin";
}

export const MessageInput = forwardRef<HTMLInputElement, MessageInputProps>(
  (
    {
      onSendMessage,
      disabled = false,
      placeholder = "How can I help you today?",
      isLoading = false,
      isMaximized = false,
      themeMode = "light",
      accessMode = "admin",
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);
    const [showTemplates, setShowTemplates] = useState(false);
    const [quickTemplates, setQuickTemplates] = useState([
      { icon: Database, text: "List all tenants", category: "Database" },
      {
        icon: Database,
        text: "Total user count",
        category: "Analytics",
      },
      {
        icon: Database,
        text: "Find tenants with violation scores above ",
        category: "Filters",
        hasInput: true,
      },
      {
        icon: FileText,
        text: "Search documents ",
        category: "Documents",
        hasInput: true,
      },
      {
        icon: FileText,
        text: "Technical support hours?",
        category: "FAQ",
      },
      { icon: Zap, text: "Explain how to ", category: "Help", hasInput: true },
    ]);

    useEffect(() => {
      // Adjust templates based on access mode
      if (accessMode === "user") {
        const newTemplates = [
          {
            icon: Database,
            text: "Show my tenant information",
            category: "Database",
          },
          {
            icon: Database,
            text: "Total user count",
            category: "Analytics",
          },
          {
            icon: Database,
            text: "What is my violation score? ",
            category: "Analytics",
          },
          {
            icon: FileText,
            text: "Search documents ",
            category: "Documents",
            hasInput: true,
          },
          {
            icon: FileText,
            text: "How to reset password?",
            category: "FAQ",
          },
          {
            icon: Zap,
            text: "Explain how to ",
            category: "Help",
            hasInput: true,
          },
        ];

        setQuickTemplates(newTemplates);
      }
    }, [accessMode]);

    // Validate input on change
    useEffect(() => {
      if (inputValue.trim()) {
        const validation = validateInput.chatMessage(inputValue);
        setValidationError(
          validation.isValid ? null : validation.error || null
        );
      } else {
        setValidationError(null);
      }
    }, [inputValue]);

    const handleSend = () => {
      if (inputValue.trim() && !disabled && !validationError) {
        onSendMessage(inputValue.trim());
        setInputValue("");
        setValidationError(null);
        setShowTemplates(false);
      }
    };

    const handleTemplateSelect = (template: (typeof quickTemplates)[0]) => {
      if (template.hasInput) {
        setInputValue(template.text);
      } else {
        onSendMessage(template.text);
      }
      setShowTemplates(false);
    };

    const getCharacterCount = () => {
      return inputValue.length;
    };

    const isNearLimit = getCharacterCount() > 3500;

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (!e.shiftKey) {
          handleSend();
        }
      }
    };

    const isDark = themeMode === "dark";

    return (
      <div
        className={`border-t ${
          isDark ? "bg-[#14161C] border-[#1C1F24]" : "bg-white border-gray-200"
        } ${isMaximized ? "rounded-b-lg" : "sm:rounded-b-2xl"}`}
      >
        {/* Templates Dropdown */}
        {showTemplates && (
          <div
            className={`border-b p-4 ${
              isDark
                ? "border-[#1C1F24] bg-[#1C1F24]"
                : "border-gray-200 bg-gradient-to-br from-white via-gray-50 to-orange-50/30"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-3 h-3 text-orange-600" />
                </div>
                <h4
                  className={`text-sm font-semibold ${isDark ? "text-gray-100" : "text-gray-800"}`}
                >
                  Quick Templates
                </h4>
              </div>
              <button
                onClick={() => setShowTemplates(false)}
                className={`p-1 rounded-md transition-colors ${
                  isDark
                    ? "text-gray-400 hover:text-gray-200 hover:bg-[#]"
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                }`}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {quickTemplates.map((template, index) => (
                <button
                  key={index}
                  onClick={() => handleTemplateSelect(template)}
                  className={`group flex items-center gap-3 p-3 text-left text-xs rounded-xl transition-all duration-200 hover:shadow-sm hover:scale-[1.02] border
                    ${
                      isDark
                        ? "bg-[#1C1F24] border-[#1C1F24] hover:bg-gray-300/5"
                        : "bg-white hover:bg-gradient-to-r hover:from-orange-50 hover:to-white border-gray-200 hover:border-orange-200"
                    }`}
                >
                  <div className="w-7 h-7 bg-orange-100 group-hover:bg-orange-200 rounded-lg flex items-center justify-center transition-colors">
                    <template.icon className="w-3.5 h-3.5 text-orange-600 group-hover:text-orange-700 transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`font-medium truncate ${
                        isDark
                          ? "text-gray-100 group-hover:text-white"
                          : "text-gray-800 group-hover:text-gray-900"
                      }`}
                    >
                      {template.text}
                    </div>
                    <div
                      className={`text-xs truncate transition-colors ${
                        isDark
                          ? "text-gray-300 group-hover:text-orange-400"
                          : "text-gray-400 group-hover:text-orange-500"
                      }`}
                    >
                      {template.category}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Input Area */}
        <div className="p-2">
          {/* Quick Actions Bar */}
          <div className="flex items-center flex-wrap gap-1 mb-2">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                isDark
                  ? "bg-[#1C1F24] hover:bg-gray-300/10 text-gray-200"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800"
              }`}
              title="Quick templates"
            >
              <Zap className="w-3 h-3" />
              <span className="hidden sm:inline">Templates</span>
            </button>
            {/* <button
              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors opacity-50"
              disabled
              title="Voice input (coming soon)"
            >
              <Mic className="w-3 h-3" />
              <span className="hidden sm:inline">Voice</span>
            </button>
            <button
              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors opacity-50"
              disabled
              title="Attach file (coming soon)"
            >
              <Paperclip className="w-3 h-3" />
              <span className="hidden sm:inline">File</span>
            </button> */}

            {/* Character Count */}
            {inputValue.length > 0 && (
              <div className="ml-auto text-xs">
                <span
                  className={
                    isNearLimit
                      ? "text-orange-600 font-medium"
                      : "text-gray-400"
                  }
                >
                  {getCharacterCount()}
                  <span className="hidden sm:inline">/4000</span>
                </span>
              </div>
            )}
          </div>

          {/* Input Field */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={ref as any}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                disabled={disabled}
                rows={1}
                className={`w-full px-3 py-1.5 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:cursor-not-allowed font-small ${
                  isDark
                    ? "bg-[#1C1F24] border-[#1C1F24] text-gray-100 placeholder-gray-400"
                    : "bg-white border-gray-200 text-gray-800 placeholder-gray-500"
                }`}
                style={{
                  height: "40px",
                  maxHeight: "40px",
                  overflow: "hidden",
                }}
              />
            </div>

            <Button
              onClick={handleSend}
              disabled={
                !inputValue.trim() || disabled || !!validationError || isLoading
              }
              className={`h-10 w-10 p-0 rounded-2xl flex items-center justify-center ${
                isDark
                  ? "bg-orange-600 hover:bg-orange-700 text-white"
                  : "bg-orange-500 hover:bg-orange-600 text-white"
              }`}
              title={isLoading ? "Sending..." : "Send message"}
            >
              {isLoading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Send className="w-6 h-6" />
              )}
            </Button>
          </div>
        </div>

        {validationError && (
          <div className="mx-4 mb-3 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
            {validationError}
          </div>
        )}

        <div className="px-2 pb-1">
          <div className="flex items-center justify-center text-xs text-gray-400">
            <span>
              Powered by <b>NopalCyber</b>
            </span>
          </div>
        </div>
      </div>
    );
  }
);

MessageInput.displayName = "MessageInput";
